import { useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";
import moment from "moment";
import { useAppDispatch, useAppSelector } from "src/store/hooks";

// Actions
import { AppStatus, inCall, setIdle, startRinging, stopRinging, togglePause } from "src/store/context/reducer";
import { getCallDetails, createCallConference, updateCallConference, markCallRefused, RefusalType } from "src/store/call/actions";
import { callDataLoaded, callEnded, initCall } from "src/store/call/reducer";

// Components
import Topbar from "./components/topbar";
import RigingComponent from "./components/ringing-screen";
import PauseComponent from "./components/pause-screen";
import LinksComponent from "./components/links-screen";

// Others
import PhoneRingAudio from "src/assets/sounds/phone-ringing.mp3";

// Services
import { ws } from "src/services/ws";
import { TextMessage } from "src/types";

// Types
import { TransferEvent } from "src/types/transfer-object";
import PSTNCall from "src/models/pstn-call";
import DialerAction from "./components/dialer-action";

export default function Home() {
  const { appStatus } = useAppSelector((state) => state.context);
  const { callDetails } = useAppSelector((state) => state.call);
  const { paused } = useAppSelector((state) => state.auth);
  const { sip } = useAppSelector((state) => state.config);

  // Hooks
  const dispatch = useAppDispatch();
  const wsBinds: Array<number> = [];

  // Refs
  const callDetailsRef = useRef(callDetails);

  // Variables
  const audio = new Audio(PhoneRingAudio);
  audio.loop = true;

  // => Methods

  // Refuse transference
  const refuseTransferEvent = (event: TransferEvent) => {
    let wsEvent = {
      type: "CALL_TRANSFER_REQUEST_REJECTED",
      data: {
        sourcePaQueueId: event.sourcePaQueueId,
      },
    };

    ws.sendMessage(wsEvent);

    return true;
  };

  // Init transfer
  const initDestCallTransferProccess = async (event: TransferEvent) => {
    try {
      let currentCallDetails = await getCallDetails(event.callId);
      const udaQueueId = event.udaQueueId;
      const sourcePaQueueId = event.sourcePaQueueId;
      const callType = currentCallDetails.data.tipo_canal;
      const hasIdPai = currentCallDetails.data.features.includes("CARROSSEL") ? currentCallDetails.data.id : null;
      const hasIdOrigem = currentCallDetails.data.id_origem || currentCallDetails.data.id;

      // Create new call
      const newCall = await createCallConference(
        currentCallDetails.data.uda.id,
        sip.paNumber,
        currentCallDetails.data.servico.id,
        currentCallDetails.data.aderente_slug,
        callType,
        currentCallDetails.data.origem_chamada,
        hasIdPai,
        hasIdOrigem
      );

      // Load from new call
      currentCallDetails = await getCallDetails(newCall.data.id);

      // Pre-fill transference variables
      currentCallDetails.data.transferenceStatus = {
        inTransference: true,
        isTransferenceOrigin: false,
        isConferenceNeeded: event.isPaConferenceNeeded,
        sourcePaQueueId: sourcePaQueueId,
      };

      try {
        currentCallDetails.data.messageList = event.webchatTextHistory ? JSON.parse(event.webchatTextHistory) : [];

        // Compatibility mode
        if (currentCallDetails.data.messageList[0] && !currentCallDetails.data.messageList[0].name) {
          currentCallDetails.data.messageList = currentCallDetails.data.messageList.map(
            (
              m: TextMessage & {
                username: string;
                message: string;
                userType: string;
              }
            ) => ({
              ...m,
              name: m.username,
              text: m.message,
              self: m.userType === "senderUser",
            })
          );
        }
      } catch (ex: any) {
        console.error("Falha ao parsear mensagens de texto", ex.message);
      }

      // Update call timestamps
      const dateTime = moment().utcOffset(-3).format("YYYY-MM-DDTHH:mm:ss");

      // Set Call started at NOW
      await updateCallConference(currentCallDetails.data.id, {
        inicio_fila: dateTime,
        inicio_conferencia: dateTime,
        aceite_chamada: dateTime,
      });

      // Dispatch load call
      dispatch(
        callDataLoaded({
          call: currentCallDetails.data,
          lastWsEvent: event,
          udaQueueId: udaQueueId,
          incomingCallId: currentCallDetails.data.id,
          twilioRoomName: event.twilioRoomName,
          twilioToken: event.twilioToken,
        })
      );

      // Update application status
      dispatch(inCall());
    } catch (ex) {
      refuseTransferEvent(event);
    }
  };

  // Receive transfer event
  const onTransferIncoming = async (event: TransferEvent) => {
    const message = event.transferMessage;
    // const isPaConferenceNeeded = event.isPaConferenceNeeded;

    // TODO: Check if app in some state that is'nt possible to receive transference

    audio.play();

    const response = await Swal.fire({
      title: "Aceitar transferencia de chamada?",
      html: message ? `<br><strong>Mensagem: </strong><textarea class="swal2-textarea" disabled rows="7" style="height: inherit;">${message}</textarea>` : "",
      focusCancel: true,
      showCancelButton: true,
      confirmButtonText: "Aceitar",
      cancelButtonText: "Cancelar",
      timer: 20000,
    });

    if (response.value) initDestCallTransferProccess(event);
    else refuseTransferEvent(event);

    audio.pause();
  };

  const onPaStartRigging = async (eventData: any) => {
    let newCall = {};

    try {
      if (eventData.callType === "ICOM_VOZ") {
        const pstnNumber = eventData.pstnNumber;

        newCall = {
          id: eventData.callId,
          aderente_slug: eventData.aderenteSlug,
          aderente_nome: eventData.aderenteSlug.charAt(0).toUpperCase() + eventData.aderenteSlug.slice(1),

          uda: {
            id: eventData.icomVozUdaToSearchId,
            usuario: {
              nome: eventData.udaName,
            },
          },
          tipo_canal: eventData.callType,
          servico: { id: eventData.serviceId },
          pstnNumbers: [
            new PSTNCall({
              numberCalled: pstnNumber,
              requestedNumber: pstnNumber,
              called: true,
            }),
          ],
        };
      } else {
        const { data } = await getCallDetails(eventData.callId!);
        newCall = {
          ...data,
          pstnNumbers: [...eventData.pstnNumbers],
        };
      }

      // Atualiza chamada
      dispatch(
        callDataLoaded({
          call: newCall,
          incomingCallId: eventData.callId,
        })
      );

      dispatch(startRinging());
      // dispatch(doStartRigging(eventData));
    } catch (error) {
      dispatch(callEnded());
    }
  };

  const onPaStopRigging = async (eventData: any) => {
    // Stop
    if (callDetails)
      await markCallRefused({
        type: RefusalType.ACTIVE,
        callId: callDetails.id,
        idUda: callDetails.uda.id,
        aderenteSlug: callDetails.aderenteSlug,
      });

    dispatch(stopRinging());
  };
  const onCallAnswered = async (eventData: any) => {
    let event = eventData;

    if (callDetailsRef.current?.callType === "ICOM_VOZ") {
      delete event["twilioRoomName"];
      delete event["twilioToken"];
    }
    dispatch(inCall());
    dispatch(initCall({ ...eventData }));
  };

  useEffect(() => {
    callDetailsRef.current = callDetails;
  }, [callDetails]);

  useEffect(() => {
    if (paused)
      dispatch(
        togglePause({
          pauseType: paused?.texto,
        })
      );
    else dispatch(setIdle());

    // Bind some events
    if (ws) {
      wsBinds.push(ws.on("PA_ANSWERED_CALL_INFO", onCallAnswered));
      wsBinds.push(ws.on("CALL_TRANSFER_REQUEST", onTransferIncoming));
      wsBinds.push(ws.on("PA_START_RINGING", onPaStartRigging));
      wsBinds.push(ws.on("PA_STOP_RINGING", onPaStopRigging));
    }

    return () => {
      ws?.clearBinds(wsBinds);

      audio.pause();
    };
  }, []);

  return (
    <div className="screen home dark:bg-neutral-gray-900 screen grow flex flex-col overflow-y-auto">
      <Topbar />

      <PauseComponent />

      <LinksComponent />

      <DialerAction />

      {appStatus === AppStatus.RINGING && callDetails && <RigingComponent />}
    </div>
  );
}
